import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import './App.css';

function Home() {
  const navigate = useNavigate();
  const { toggleTheme, isDark } = useTheme();

  // Get current user from localStorage
  const getCurrentUser = () => {
    try {
      const authData = localStorage.getItem('auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.user;
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
    return null;
  };

  const handleLogout = () => {
    // Clear authentication data from localStorage
    localStorage.removeItem('auth');
    // Redirect to login (AuthWrapper will handle showing login page)
    window.location.reload();
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
      {/* Header with Perzsi Branding */}
      <header className="perzsi-gradient shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div
                className="cursor-pointer"
                onClick={() => navigate('/')}
              >
                <img
                  src="/perzsi-icon-black.png"
                  alt="Perzsi.com"
                  className="h-12 w-auto"
                />
                <p className="text-yellow-100">Impact Data Management Hub</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-white text-right">
                <p className="text-sm opacity-90">Comprehensive Data Management Platform</p>
                {getCurrentUser() && (
                  <p className="text-xs opacity-75">
                    Logged in as: {getCurrentUser().email}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={toggleTheme}
                  className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors duration-200"
                  title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
                >
                  {isDark ? '☀️' : '🌙'}
                </button>
                {getCurrentUser() && (
                  <button
                    onClick={handleLogout}
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                  >
                    Logout
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-12">
          <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>
            Impact Data Management Platform
          </h1>
          <p className={`text-xl max-w-3xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Manage and analyze your Impact Radius affiliate data with powerful tools for ads, products, stores, and promotions.
          </p>
        </div>

        {/* Main Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {/* Ads Management */}
          <div className="perzsi-card cursor-pointer transform hover:scale-105 transition-transform duration-200"
               onClick={() => handleNavigation('/ads')}>
            <div className="text-center">
              <div className="text-4xl mb-4">📊</div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Ads Management</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Manage affiliate advertisements, filter by type and date, transform to banner format
              </p>
              <div className={`space-y-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <div>• Fetch & Filter Ads</div>
                <div>• Banner Transformation</div>
                <div>• CSV Export</div>
              </div>
            </div>
          </div>

          {/* Stores and Products Management */}
          <div className="perzsi-card cursor-pointer transform hover:scale-105 transition-transform duration-200"
               onClick={() => handleNavigation('/stores')}>
            <div className="text-center">
              <div className="text-4xl mb-4">🏪</div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Stores and Products Management</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Fetch and manage affiliate store data with product catalog browsing
              </p>
              <div className={`space-y-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <div>• Store-Group Directory</div>
                <div>• Product Catalog Browser</div>
                <div>• Fresh API Data</div>
              </div>
            </div>
          </div>

          {/* Promotions Management */}
          <div className="perzsi-card cursor-pointer transform hover:scale-105 transition-transform duration-200 opacity-75">
            <div className="text-center">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Promotions Management</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Track and manage promotional campaigns (Coming Soon)
              </p>
              <div className={`space-y-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <div>• Campaign Tracking</div>
                <div>• Promotion Analytics</div>
                <div>• Deal Management</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="perzsi-card text-center">
            <h4 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>API Integration</h4>
            <p className="text-2xl font-bold text-green-600">✅</p>
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Impact Radius API Connected</p>
          </div>

          <div className="perzsi-card text-center">
            <h4 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Data Processing</h4>
            <p className="text-2xl font-bold text-blue-600">🔄</p>
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Real-time Data Filtering</p>
          </div>

          <div className="perzsi-card text-center">
            <h4 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Export Ready</h4>
            <p className="text-2xl font-bold text-purple-600">📥</p>
            <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>CSV & Excel Export</p>
          </div>
        </div>

        {/* Getting Started Guide */}
        <div className="perzsi-card">
          <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-perzsi-gray'}`}>Getting Started</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>🚀 Quick Start:</h4>
              <ol className={`list-decimal list-inside space-y-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Click on "Ads Management" to start with advertisements</li>
                <li>Use "Stores and Products Management" to browse stores and products</li>
                <li>Apply filters to find specific data</li>
                <li>Export results as CSV for further analysis</li>
              </ol>
            </div>
            <div>
              <h4 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>📋 Prerequisites:</h4>
              <ul className={`list-disc list-inside space-y-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Impact API credentials in .env file</li>
                <li>Python backend server running</li>
                <li>React development server active</li>
                <li>Stores data fetched for products</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
